import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { OPEN_HALT_EVENT } from '@/lib/palette';
import { useCan } from '@/lib/permissions';
import { qk, useApiMutation } from '@/lib/queries';

/** Global kill switch (owner/admin). Halting requires typing HALT. */
export function KillSwitch({ halted }: { halted: boolean }) {
  const { t } = useTranslation();
  const allowed = useCan('org:halt');
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const invalidate = [qk.orgStatus, ['runs'], qk.agents];
  const halt = useApiMutation('orgHalt', { invalidate, success: 'Organization halted' });
  const resume = useApiMutation('orgResume', { invalidate, success: 'Organization resumed' });
  // The launcher's "Halt all agents" opens this same typed-HALT confirmation.
  useEffect(() => {
    const open = () => setOpen(true);
    window.addEventListener(OPEN_HALT_EVENT, open);
    return () => window.removeEventListener(OPEN_HALT_EVENT, open);
  }, []);
  if (!allowed) return null;

  if (halted) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="success" size="sm" aria-label={t('shell.resumeTitle')} className="mr-1">
            ▶ <span className="hidden sm:inline">{t('shell.resume')}</span>
          </Button>
        </DialogTrigger>
        <DialogContent
          title={t('shell.resumeTitle')}
          description={t('shell.resumeBody')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                loading={resume.isPending}
                onClick={() => resume.mutate({ body: {} }, { onSuccess: () => setOpen(false) })}
              >
                {t('shell.resume')}
              </Button>
            </>
          }
        />
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setTyped('');
      }}
    >
      <DialogTrigger asChild>
        <Button variant="danger" size="sm" aria-label={t('shell.killTitle')} className="mr-1">
          ■ <span className="hidden sm:inline">{t('shell.killSwitch')}</span>
        </Button>
      </DialogTrigger>
      <DialogContent
        title={<span className="text-danger">{t('shell.killTitle')}</span>}
        description={t('shell.killBody')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              disabled={typed !== 'HALT'}
              loading={halt.isPending}
              onClick={() =>
                halt.mutate({ body: { reason: reason || undefined } }, { onSuccess: () => setOpen(false) })
              }
            >
              ■ {t('shell.killConfirm')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label={t('common.reason')} htmlFor="halt-reason">
            <Input id="halt-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <Field label={t('shell.killTypeHalt')} htmlFor="halt-confirm">
            <Input
              id="halt-confirm"
              prompt
              autoComplete="off"
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="HALT"
              aria-describedby="halt-help"
            />
          </Field>
        </div>
      </DialogContent>
    </Dialog>
  );
}
